import { useVisualViewport } from '../hooks/useVisualViewport';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { helpScreenForPath, type PermissionModule } from '@alexiarest/shared';
import { veSeccion, type Acceso } from '../auth/roles';
import { useAuth } from '../auth/AuthContext';
import { isDemoMode } from '../api/client';
import { useMe, useZonaHorariaAutomatica } from '../api/hooks';
import { useAparienciaDelPerfil } from '../context/ThemeContext';
import { activeCurrency, activeLocale, configureLocale, configureMoney } from '../format';
import { HelpPanel } from '../help/HelpPanel';
import { ScopePicker } from './ScopePicker';
import { FounderTrialBanner } from './FounderTrialBanner';
import { PagoPendienteBanner } from './PagoPendienteBanner';
import { useContadoresMenu, type ContadorMenu } from '../hooks/useContadoresMenu';
import { TrialPill } from './TrialPill';
import { SinPlanProvider } from './SinPlanBloqueo';
import { PreguntarAlexiaProvider, type PreguntarAlexia } from './PreguntarAlexia';
import { InstallAppButton } from './InstallAppButton';
import { Novedades, abrirNovedades } from './novedades/NovedadesPopup';



interface NavItem {
  to: string;
  label: string;
  /** Seccion de permisos a la que pertenece. Ver auth/roles.ts. */
  modulo: PermissionModule;
  /** Otras secciones que también dan acceso (Alexia: preguntar, recetas o gastos). */
  alternativas?: PermissionModule[];
  end?: boolean;
}

/*
 * Menu por bloques (rediseño de Alex, 10 oct 2026). Panel y Alexia siempre a
 * la vista; Costeo y Finanzas se pliegan y son los mismos nombres que tendran
 * los modulos en los planes. Lo que se configura una vez y casi no se visita
 * (Objetivos, Configuracion) baja al pie.
 */
const GENERAL: NavItem[] = [
  { to: '/', label: 'Panel general', modulo: 'panel', end: true },
  { to: '/alexia', label: 'Alexia', modulo: 'asistente', alternativas: ['recetas', 'gastos'] },
];

// Orden del camino natural (rediseño, 10 oct 2026): ingredientes →
// preparaciones → tu carta; luego recuento y proveedores. «Recetas» y
// «Precios de la carta» se juntaron en «Tu carta».
const COSTEO: NavItem[] = [
  { to: '/ingredientes', label: 'Ingredientes', modulo: 'ingredientes' },
  { to: '/preparaciones', label: 'Preparaciones', modulo: 'recetas' },
  { to: '/carta', label: 'Tu carta', modulo: 'recetas', alternativas: ['precios'] },
  { to: '/inventario', label: 'Recuento mensual', modulo: 'inventario' },
  { to: '/proveedores', label: 'Proveedores', modulo: 'proveedores' },
];

// Tesoreria queda fuera del menu en el MVP (decision de la reunion con Alex:
// demasiado trabajo manual por ahora). La ruta, el motor y la API siguen
// vivos: volvera al menu cuando el importador de datos le quite la friccion.
const FINANZAS: NavItem[] = [
  { to: '/ingresos', label: 'Ventas', modulo: 'ingresos' },
  { to: '/gastos', label: 'Gastos', modulo: 'gastos' },
  { to: '/resultados', label: 'Estado de resultados', modulo: 'resultados' },
  { to: '/analisis', label: 'Análisis', modulo: 'analisis' },
  { to: '/inversiones', label: 'Inversiones', modulo: 'inversiones' },
];

type BloqueId = 'costeo' | 'finanzas';

/**
 * Pantallas sin selector de local ni periodo (rediseño, oct 2026): Alexia
 * mira todos los datos y un precio es el de hoy.
 */
// Objetivos (bloque I): valen para siempre y llevan su propio selector de local.
// Configuración, sin selector de periodo (bloque C, 14 oct 2026: «no pinta nada aquí»).
const RUTAS_SIN_PERIODO = new Set(['/alexia', '/ingredientes', '/preparaciones', '/proveedores', '/objetivos', '/ajustes']);
/** Ventas lleva su propio «‹ mes ›»: arriba solo el local, si hay más de uno. */
// Inversiones (bloque I): siempre a fecha de hoy, sin periodo; sí el local.
// Estado de resultados y Análisis también (bloque R): llevan su mes o sus
// meses cerrados.
const RUTAS_SOLO_LOCAL = new Set(['/ingresos', '/carta', '/inventario', '/inversiones', '/resultados', '/analisis']);

/**
 * Cada apartado del menu se filtra por la seccion a la que pertenece. Vale
 * igual para los roles fijos (el encargado no ve la P&L, cocina solo ve
 * Costeo) y para un rol personalizado, porque la pregunta es la misma. La API
 * los veta igualmente (403); esto solo evita ensenyar puertas cerradas.
 */
function itemsVisibles(items: NavItem[], acceso: Acceso): NavItem[] {
  return items.filter((item) => veSeccion(acceso, item.modulo) || (item.alternativas ?? []).some((modulo) => veSeccion(acceso, modulo)));
}

/** El numero se ve; el texto («2 facturas vencidas») va como descripcion del enlace, no en su nombre. */
function Contador({ valor }: { valor?: ContadorMenu }): JSX.Element | null {
  if (!valor || valor.n <= 0) return null;
  return <span className={`nav__count nav__count--${valor.tono}`} title={valor.titulo} aria-hidden="true">{valor.n}</span>;
}

/** Fuera del enlace: si estuviera dentro, entraria en su nombre («Gastos 2 facturas vencidas»). */
function DescripcionContador({ valor, id }: { valor?: ContadorMenu; id: string }): JSX.Element | null {
  if (!valor || valor.n <= 0) return null;
  return <span className="sr-only" id={id}>{valor.titulo}</span>;
}

const idContador = (to: string) => `nav-aviso-${to.replace(/\W/g, '') || 'panel'}`;

function NavItemLink({ item, contador, sub = false }: { item: NavItem; contador?: ContadorMenu; sub?: boolean }): JSX.Element {
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        aria-describedby={contador && contador.n > 0 ? idContador(item.to) : undefined}
        className={({ isActive }) => `nav__link${sub ? ' nav__link--sub' : ''}${isActive ? ' is-active' : ''}`}
      >
        {!sub && <NavIcono ruta={item.to} />}
        <span className="nav__label">{item.label}</span>
        <Contador valor={contador} />
      </NavLink>
      <DescripcionContador valor={contador} id={idContador(item.to)} />
    </li>
  );
}

/**
 * Un bloque plegable. El titulo solo abre y cierra: no lleva a ninguna
 * pantalla. Cerrado, suma los avisos de dentro para que plegar no sea
 * esconder; abierto, cada aviso va en su pantalla.
 */
function NavBloque({ id, titulo, items, abierto, onToggle, contadores }: {
  id: BloqueId;
  titulo: string;
  items: NavItem[];
  abierto: boolean;
  onToggle: () => void;
  contadores: Partial<Record<string, ContadorMenu>>;
}): JSX.Element | null {
  if (items.length === 0) return null;
  const dentro = items.map((item) => contadores[item.to]).filter((c): c is ContadorMenu => Boolean(c && c.n > 0));
  // Cerrado: rojo si dentro hay dinero en riesgo; si no, ambar.
  const resumen: ContadorMenu | undefined = dentro.length
    ? { n: dentro.reduce((s, c) => s + c.n, 0), tono: dentro.some((c) => c.tono === 'bad') ? 'bad' : 'warn', titulo: dentro.map((c) => c.titulo).join(' · ') }
    : undefined;
  return (
    <div className="nav__section">
      <button type="button" className="nav__block" aria-expanded={abierto} aria-controls={`nav-${id}`} aria-describedby={!abierto && resumen ? `nav-aviso-${id}` : undefined} onClick={onToggle}>
        <NavIcono ruta={id} />
        <span className="nav__label">{titulo}</span>
        {!abierto && <Contador valor={resumen} />}
        <svg className="nav__chevron" width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 2 L10 7 L4 12" /></svg>
      </button>
      {!abierto && <DescripcionContador valor={resumen} id={`nav-aviso-${id}`} />}
      {abierto && (
        <ul className="nav__list nav__list--sub" id={`nav-${id}`}>
          {items.map((item) => <NavItemLink key={item.to} item={item} contador={contadores[item.to]} sub />)}
        </ul>
      )}
    </div>
  );
}

/** El bloque al que pertenece una ruta, si pertenece a alguno. */
function bloqueDeRuta(pathname: string): BloqueId | undefined {
  const dentro = (items: NavItem[]) => items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
  if (dentro(COSTEO)) return 'costeo';
  if (dentro(FINANZAS)) return 'finanzas';
  return undefined;
}

/**
 * Que bloques deja abiertos cada usuario. Es una comodidad de este navegador:
 * si se pierde, el menu vuelve a abrir el bloque por el que empezo en el alta.
 */
function leerBloques(clave: string): Record<BloqueId, boolean> | null {
  try {
    const raw = localStorage.getItem(clave);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Record<BloqueId, boolean>>;
    return { costeo: Boolean(parsed.costeo), finanzas: Boolean(parsed.finanzas) };
  } catch {
    return null;
  }
}

function guardarBloques(clave: string, valor: Record<BloqueId, boolean>): void {
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    /* sin almacenamiento: el menu se abrira igual la proxima vez */
  }
}

export function Layout(): JSX.Element {
  useVisualViewport();
  const { user, logout, isDemo } = useAuth();
  const location = useLocation();

  /*
   * Multi-mercado: la moneda del tenant manda en todos los importes. Cuando
   * llega (o cambia en Ajustes) se reconfiguran los formateadores y se repinta
   * el contenido para que ninguna cifra quede en la moneda vieja.
   *
   * La diferencia importante es REPINTAR vs REMONTAR. Al arrancar, la moneda
   * llega despues del primer render, y remontar ahi le borra a la pantalla
   * todo lo que hubiera leido al montarse. Se veia en cualquier restaurante
   * que no fuera del euro: la vuelta de Stripe (/ajustes?pago=ok) aterrizaba
   * en la pestanya equivocada porque Ajustes se montaba dos veces y la segunda
   * ya no tenia el parametro. Asi que la primera vez solo se repinta.
   *
   * Un cambio POSTERIOR (en Ajustes) si remonta: ahi ya hay cifras pintadas y
   * valores memorizados que no se enterarian de otra forma.
   */
  const me = useMe();
  // Este estado solo existe para repintar; la clave del remontaje va aparte.
  const [, setMonedaActiva] = useState(activeCurrency());
  const claveContenido = useRef(activeCurrency());
  const primeraMoneda = useRef(true);
  const tenantCurrency = me.data?.tenant?.currency;
  const tenantCountry = me.data?.tenant?.country;
  useEffect(() => {
    if (!tenantCurrency) return;
    // El locale de las cifras va con el pais (mejora 45); se trata igual que
    // la moneda: repintar la primera vez, remontar si cambia en Ajustes.
    const cambioLocale = configureLocale(tenantCountry, tenantCurrency);
    const haCambiado = configureMoney(tenantCurrency) || cambioLocale;
    if (haCambiado && !primeraMoneda.current) claveContenido.current = `${activeCurrency()}-${activeLocale()}`;
    primeraMoneda.current = false;
    if (haCambiado) setMonedaActiva(activeCurrency());
  }, [tenantCurrency, tenantCountry]);

  // «Apariencia» guardada en el perfil de la persona (tokens v3, 14 oct 2026).
  useAparienciaDelPerfil(me.data?.user?.apariencia);

  // En movil la navegacion es un panel que se abre por encima del contenido.
  // Apilarla arriba costaba una pantalla entera de menu antes de ver un solo
  // numero, cada vez que se entraba.
  const [menuAbierto, setMenuAbierto] = useState(false);
  const botonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const cerrar = useCallback(() => setMenuAbierto(false), []);

  // Al navegar se cierra solo: tocar un enlace y que el menu siga tapando lo
  // que acabas de abrir es de las cosas que mas molestan en un movil.
  useEffect(() => {
    setMenuAbierto(false);
  }, [location.pathname]);

  // Escape cierra y devuelve el foco al boton, que es de donde salio.
  useEffect(() => {
    if (!menuAbierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const elements = [...(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)') ?? [])].filter((el) => el.getClientRects().length > 0);
        const first = elements[0]; const last = elements[elements.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
      if (e.key === 'Escape') {
        setMenuAbierto(false);
        botonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuAbierto]);

  // Con el panel abierto, el fondo no debe poder desplazarse.
  useEffect(() => {
    if (!menuAbierto) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [menuAbierto]);

  // Al abrir, el foco entra en el panel para que un lector de pantalla no
  // siga leyendo el contenido de detras.
  useEffect(() => {
    if (menuAbierto) panelRef.current?.focus();
  }, [menuAbierto]);

  // Ayuda (Alex, 5 sep 2026): el panel sabe en qué pantalla está por la ruta.
  /*
   * Altura de la barra superior, publicada como variable CSS: es lo que
   * necesitan las cabeceras fijas de las tablas para pegarse justo debajo
   * (mejora 48). Se mide porque cambia con el selector de periodo.
   */
  const topbarRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = topbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const publicar = () =>
      document.documentElement.style.setProperty('--topbar-alto', `${el.offsetHeight}px`);
    publicar();
    const ro = new ResizeObserver(publicar);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  /*
   * «Pregúntale a Alexia» desde un «?» (15 oct 2026): abre la Ayuda de esta
   * pantalla con la pregunta ya enviada. `n` distingue dos veces la misma
   * pregunta. Sin chat (cocina, encargado…) no hay enlace.
   */
  const [preguntaAyuda, setPreguntaAyuda] = useState<{ texto: string; n: number } | null>(null);
  const nPregunta = useRef(0);
  const pantalla = useMemo(() => helpScreenForPath(location.pathname, location.search) ?? null, [location.pathname, location.search]);
  useZonaHorariaAutomatica(me.data);
  const role = me.data?.user?.role;
  const acceso: Acceso = { role, permissions: me.data?.user?.permissions };
  const cargadoMe = Boolean(me.data);
  const contadores = useContadoresMenu(acceso, cargadoMe);
  const conChat = cargadoMe && veSeccion(acceso, 'asistente');
  const preguntar = useMemo<PreguntarAlexia>(
    () => (conChat ? (texto: string) => { nPregunta.current += 1; setPreguntaAyuda({ texto, n: nPregunta.current }); setAyudaAbierta(true); } : null),
    [conChat],
  );

  /*
   * Bloques abiertos. Reglas (Alex, 10 oct 2026): el de la pantalla en la que
   * estas se abre solo; se recuerda lo que deja abierto cada usuario; la
   * primera vez, abierto el bloque por el que empezo en el alta.
   */
  const claveBloques = `alexia:menu:${me.data?.user?.userId ?? 'anon'}`;
  const inicialBloques = (): Record<BloqueId, boolean> => {
    const guardado = leerBloques(claveBloques);
    if (guardado) return guardado;
    const empezoPorNumeros = me.data?.tenant?.onboardingGoal === 'numbers';
    return { costeo: !empezoPorNumeros, finanzas: empezoPorNumeros };
  };
  const [bloques, setBloques] = useState<Record<BloqueId, boolean>>(inicialBloques);
  // /me llega despues del primer render: entonces ya se sabe de quien es el menu.
  const bloquesDe = useRef<string | null>(null);
  useEffect(() => {
    if (!me.data || bloquesDe.current === claveBloques) return;
    bloquesDe.current = claveBloques;
    setBloques(inicialBloques());
  }, [claveBloques, me.data]);
  const bloqueActual = bloqueDeRuta(location.pathname);
  useEffect(() => {
    if (!bloqueActual) return;
    setBloques((prev) => {
      if (prev[bloqueActual]) return prev;
      const next = { ...prev, [bloqueActual]: true };
      guardarBloques(claveBloques, next);
      return next;
    });
  }, [bloqueActual, claveBloques]);
  const alternarBloque = (id: BloqueId) => setBloques((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    guardarBloques(claveBloques, next);
    return next;
  });

  const mobileItems = itemsVisibles([
    { to: '/', label: 'Inicio', modulo: 'panel', end: true },
    { to: '/alexia', label: 'Alexia', modulo: 'asistente', alternativas: ['recetas', 'gastos'] },
    { to: '/gastos', label: 'Gastos', modulo: 'gastos' },
    { to: '/analisis', label: 'Análisis', modulo: 'analisis' },
  ], acceso);
  /*
   * El tour de 14 pasos se fue el 15 oct 2026 (sección 17 del canvas de
   * Alex): explicaba pantallas vacías. Ahora la app enseña mientras la
   * persona hace cosas: «Para empezar» en el Panel y un aviso pequeño la
   * primera vez que entra a cada pantalla (components/AvisoPrimeraVez).
   */

  return (
    <PreguntarAlexiaProvider value={preguntar}>
    <SinPlanProvider>
    <div className={`shell ${menuAbierto ? 'shell--menu-abierto' : ''} ${location.pathname === '/alexia' ? 'shell--chat' : ''}`.trim()}>
      <a className="skip-link" href="#contenido">
        Saltar al contenido
      </a>

      {/* Cabecera compacta: solo se ve en movil */}
      <header className="mobile-bar">
        <button
          ref={botonRef}
          type="button"
          className="mobile-bar__menu"
          aria-expanded={menuAbierto}
          aria-controls="navegacion-principal"
          onClick={() => setMenuAbierto((v) => !v)}
        >
          <IconoMenu abierto={menuAbierto} />
          <span className="sr-only">{menuAbierto ? 'Cerrar menú' : 'Abrir menú'}</span>
        </button>
        <Logotipo className="mobile-bar__logo" />
        <div className="mobile-bar__acciones">
          <TrialPill compacto />
          <BotonAyuda onClick={() => setAyudaAbierta(true)} compacto />
        </div>
      </header>

      {menuAbierto && <div className="backdrop" onClick={cerrar} aria-hidden="true" />}

      <aside className="sidebar" id="navegacion-principal" ref={panelRef} tabIndex={-1}>
        <button type="button" className="mobile-menu-close" onClick={cerrar} aria-label="Cerrar navegación">Cerrar ×</button>
        <div className="brand">
          {/* Logotipo del sistema de diseno, con el texto ya en curvas: no
              depende de que Bricolage Grotesque haya cargado. */}
          <Logotipo className="brand__logo" />
        </div>

        <nav className="nav" aria-label="Navegación principal">
          <ul className="nav__list">
            {itemsVisibles(GENERAL, acceso).map((item) => <NavItemLink key={item.to} item={item} contador={contadores[item.to]} />)}
          </ul>
          <NavBloque id="costeo" titulo="Costeo" items={itemsVisibles(COSTEO, acceso)} abierto={bloques.costeo} onToggle={() => alternarBloque('costeo')} contadores={contadores} />
          <NavBloque id="finanzas" titulo="Finanzas" items={itemsVisibles(FINANZAS, acceso)} abierto={bloques.finanzas} onToggle={() => alternarBloque('finanzas')} contadores={contadores} />
        </nav>

        <div className="sidebar__foot">
          <ul className="nav__list nav__list--pie">
            {itemsVisibles([{ to: '/objetivos', label: 'Objetivos', modulo: 'objetivos' }, { to: '/ajustes', label: 'Configuración', modulo: 'ajustes' }], acceso).map((item) => <NavItemLink key={item.to} item={item} sub />)}
            {/* Administración es el panel de la PLATAFORMA: solo Pol y Alex
                (y en la demo, para enseñarlo). Ningún cliente la ve. */}
            {me.data?.user?.platformAdmin && (
              <li>
                <NavLink to="/admin" className={({ isActive }) => `nav__link nav__link--sub${isActive ? ' is-active' : ''}`}>
                  <span className="nav__label">Administración</span>
                  <span className="nav__tag">solo equipo</span>
                </NavLink>
              </li>
            )}
            <li>
              {/* Soporte vive dentro de la ayuda: el botón flotante tapaba contenido. */}
              <button type="button" className="nav__link nav__link--sub" data-tour="soporte" onClick={() => setAyudaAbierta(true)}>
                <span className="nav__label">Ayuda y soporte</span>
              </button>
            </li>
          </ul>
          <InstallAppButton />
          {isDemo && (
            <p className="sidebar__demo">
              Modo demostración: los datos son de ejemplo y no se guardan.
            </p>
          )}
          <div className="sidebar__cuenta">
            <span className="sidebar__inicial" aria-hidden="true">{(user?.name || user?.email || '?').trim().charAt(0).toUpperCase()}</span>
            <div className="sidebar__cuenta-datos">
              <span className="sidebar__email" title={user?.email}>{user?.email || user?.name || 'Invitado'}</span>
              {!isDemoMode && (
                <button type="button" className="sidebar__salir" onClick={logout}>
                  Cerrar sesión
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>

      <div className="main">
        {/* Cobro fallido: arriba del todo y en todas las pantallas. Ver mejora 69. */}
        <PagoPendienteBanner />
        <FounderTrialBanner />
        <header className={`topbar${location.pathname === '/alexia' ? ' topbar--chat' : ''}`} ref={topbarRef}>
          {/* Alexia mira todos los datos: el periodo se le dice en el chat. */}
          {/* Rediseño (Alex, 12 oct 2026): todo arriba a la derecha y discreto: «Local ▾», «Este mes ▾» y Ayuda. */}
          <span className="topbar__hueco" />
          {RUTAS_SIN_PERIODO.has(location.pathname) || location.pathname.startsWith('/recetas/') ? null : <ScopePicker soloLocal={RUTAS_SOLO_LOCAL.has(location.pathname)} />}
          <div className="topbar__actions">
            {/* Fin de la prueba (sección 18, 15 oct 2026): la etiqueta vuelve arriba, junto a Ayuda. */}
            <TrialPill />
            <BotonAyuda onClick={() => setAyudaAbierta(true)} />
          </div>
        </header>
        <main className="content" id="contenido" key={claveContenido.current}>
          <Outlet />
        </main>
      </div>

      <nav className="mobile-tabs" aria-label="Accesos rápidos">
        {mobileItems.map((item) => <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `mobile-tabs__item${isActive ? ' is-active' : ''}`}>
          <MobileIcon path={item.to} /><span>{item.label}</span>
        </NavLink>)}
        <button type="button" className={`mobile-tabs__item${menuAbierto ? ' is-active' : ''}`} aria-expanded={menuAbierto} aria-controls="navegacion-principal" onClick={() => setMenuAbierto((v) => !v)}>
          <IconoMenu abierto={menuAbierto} /><span>Menú</span>
        </button>
      </nav>

      <HelpPanel
        open={ayudaAbierta}
        // Al cerrar, la pregunta del «?» ya se hizo: no vuelve a enviarse al reabrir.
        onClose={() => { setAyudaAbierta(false); setPreguntaAyuda(null); }}
        screen={pantalla}
        role={role}
        acceso={acceso}
        pregunta={preguntaAyuda}
        // Ayuda › Novedades: cierra la ayuda y vuelve a abrir el pop-up.
        onNovedades={() => { setAyudaAbierta(false); setPreguntaAyuda(null); abrirNovedades(); }}
      />
      {/* «Alexia tiene nueva cara» (18 oct 2026): una vez, a quien tenía cuenta antes del lanzamiento. */}
      <Novedades />
    </div>
    </SinPlanProvider>
    </PreguntarAlexiaProvider>
  );
}

/** El logotipo con el color de cada tema (tinta oscura en claro, crema en oscuro). */
function Logotipo({ className }: { className: string }): JSX.Element {
  return (
    <>
      <img className={`${className} logo-tema--claro`} src="/logo/logotipo-claro.svg" alt="Alexia Rest" width={217} height={48} />
      <img className={`${className} logo-tema--oscuro`} src="/logo/logotipo-oscuro.svg" alt="" aria-hidden="true" width={217} height={48} />
    </>
  );
}

/** Iconos finos, del mismo color que el texto, solo en los elementos principales. */
function NavIcono({ ruta }: { ruta: string }): JSX.Element | null {
  // La «a» va en CSS: así no forma parte del nombre del enlace («Alexia», no «aAlexia»).
  if (ruta === '/alexia') return <span className="nav__avatar" aria-hidden="true" />;
  const trazo = ruta === '/' ? 'M3 11 L12 4 L21 11M5 10v10h14V10'
    : ruta === 'costeo' ? 'M6 3v8a3 3 0 0 0 6 0V3M9 3v18M17 3c-2 2-2 6 0 8v10'
    : ruta === 'finanzas' ? 'M4 20V10M10 20V4M16 20v-7M22 20H2'
    : null;
  if (!trazo) return null;
  return <svg className="nav__icono" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={trazo} /></svg>;
}

function MobileIcon({ path }: { path: string }): JSX.Element {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {path === '/' ? <path d="m3 10 9-7 9 7v11h-6v-7H9v7H3Z" /> : path === '/alexia' ? <path d="M21 11a9 9 0 0 1-9 9H4l-2 2V11a9 9 0 0 1 19 0ZM7 10h10M7 14h6" /> : path === '/gastos' ? <path d="M5 3h14v18l-3-2-4 2-4-2-3 2ZM8 7h8M8 11h8M8 15h4" /> : <path d="M4 20V10m8 10V4m8 16v-7" />}
  </svg>;
}

/**
 * Boton «Ayuda» de la cabecera. Va en la barra de escritorio y en la de
 * movil (la primera no se ve en movil); el tour resalta el que este visible.
 */
function BotonAyuda({ onClick, compacto = false }: { onClick: () => void; compacto?: boolean }): JSX.Element {
  return (
    <button
      type="button"
      className={`help-btn ${compacto ? 'help-btn--compacto' : ''}`.trim()}
      onClick={onClick}
      data-tour="ayuda"
      aria-label="Ayuda de esta pantalla"
      title="Ayuda de esta pantalla"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5v.2" />
        <path d="M12 17h.01" />
      </svg>
      {!compacto && <span>Ayuda</span>}
    </button>
  );
}

function IconoMenu({ abierto }: { abierto: boolean }): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {abierto ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
    </svg>
  );
}
